import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Col, Row } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import { ProjectSummaryVM, IProjectSummaryProps } from './ProjectSummaryVM';
import stores from 'cd-eft/models/Stores';
import './ProjectSummary.scss';

const libraryStore = stores.AppStore.projectsLibraryStore;

const ProjectSummaryComponent = (props: IProjectSummaryProps) => {
  const vm = useRef(new ProjectSummaryVM(props)).current;

  useEffect(() => {
    vm.getNewOwnerInfo();
  }, []);

  return (
    <>
      <div className="container-wrapper d-flex flex-column h-100">
        <Col className="project-summary-header">
          <h2 className="project-summary">Project Summary</h2>
        </Col>

        {!vm.editProjectSummary && (
          <>
            <Col className="p-3 flex-fill">
              <Row className="info align-items-start">
                <Col>
                  <Row>
                    <Col>Project Status:</Col>
                    <Col>
                      <div className={`status-level m-0 ${vm.project.projectStatus}`}>
                        {vm.project.projectStatus}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>Client Name:</Col>
                    <Col className="nobr">{vm.project.client ? vm.project.client : ''}</Col>
                  </Row>
                  <Row>
                    <Col>Designed By:</Col>
                    <Col className="capitalize nobr">{vm.project.designedBy.split('@')[0].replace('.', ' ')}</Col>
                  </Row>
                  <Row>
                    <Col>Job Class:</Col>
                    <Col>{vm.project.jobClass ? vm.project.jobClass : ''}</Col>
                  </Row>
                </Col>
                <Col>
                  <Row>
                    <Col>Project Type:</Col>
                    <Col>{vm.project.projectClassId === 1 ? 'Design' : 'Analysis'}</Col>
                  </Row>
                  <Row>
                    <Col>Project Owner:</Col>
                    <Col className="capitalize nobr">{vm.newOwnerName}</Col>
                  </Row>
                  <Row>
                    <Col>County/State:</Col>
                    <Col>
                      {`${vm.project.county ? vm.project.county + ', ' : ''}${vm.project.state ? vm.project.state : ''
                        }`}
                    </Col>
                  </Row>
                  <Row>
                    <Col>Projection Information:</Col>
                    <Col>{libraryStore.projectProjectionInfo}</Col>
                  </Row>
                </Col>
              </Row>
            </Col>

            <Col className="text-right">
              <button className="usa-button m-0 mb-3" onClick={vm.handleEdit}>
                Edit
              </button>
            </Col>
          </>
        )}

        {vm.editProjectSummary && (
          <div className="project-summary-edit-form">
            <h6>
              Fields marked with an&nbsp;
              <span>
                asterisk(
                <Icon path={mdiAsterisk} title="Required Icon" size={0.4} color="red" />)
              </span>
              &nbsp;are required. Only numbers and letters allowed.
            </h6>
            <div className="info">
              <div className="col">
                <div>
                  <div className="form-row-fixed-height">
                    <label className="usa-label" htmlFor="projectTitle">
                      Project Name
                      <Icon path={mdiAsterisk} title="Required Icon" size={0.4} color="red" />:
                    </label>
                    <input
                      className={vm.errorClass('projectTitle')}
                      id="projectTitle"
                      aria-label="Project Title"
                      name="projectTitle"
                      type="text"
                      required
                      defaultValue={vm.projectSummaryForm.projectTitle}
                      onChange={vm.handleInputChange}
                    />
                  </div>
                  <span className="usa-error-message" id="projectTitle-error--message" role="alert">
                    <div>{vm.formValidation.formErrors['projectTitle']}</div>
                  </span>
                </div>
                <div className="form-row-fixed-height">
                  <label className="usa-label" htmlFor="projectStatusId">
                    Project Status
                    <Icon path={mdiAsterisk} title="Required Icon" size={0.4} color="red" />:
                  </label>
                  <select
                    className="usa-select"
                    name="projectStatusId"
                    id="projectStatusId"
                    aria-label="Project Status"
                    data-testid="the-SelectOption"
                    defaultValue={vm.projectSummaryForm.projectStatusId}
                    onChange={vm.handleInputChange}
                    required
                  >
                    {libraryStore.projectStatuses.map((el) => (
                      <option className="projectStatus" value={el.id} key={el.name} label={el.description}>
                        {el.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="form-row-fixed-height">
                    <label className="usa-label" htmlFor="client">
                      Client Name
                      <Icon path={mdiAsterisk} title="Required Icon" size={0.4} color="red" />:
                    </label>
                    <input
                      className={'usa-input' + vm.errorClass('client')}
                      id="client"
                      aria-label="Client Name"
                      name="client"
                      type="text"
                      required
                      defaultValue={vm.projectSummaryForm.client}
                      onChange={vm.handleInputChange}
                    />
                  </div>
                  <span className="usa-error-message" id="client-error--message" role="alert">
                    <div>{vm.formValidation.formErrors['client']}</div>
                  </span>
                </div>
                <div>
                  <div className="form-row-fixed-height">
                    <label className="usa-label" htmlFor="designedBy">
                      Designed By
                      <Icon path={mdiAsterisk} title="Required Icon" size={0.4} color="red" />:
                    </label>
                    <input
                      className={'usa-input' + vm.errorClass('designedBy')}
                      id="designedBy"
                      aria-label="Designed By"
                      name="designedBy"
                      type="text"
                      required
                      defaultValue={vm.projectSummaryForm.designedBy}
                      onChange={vm.handleInputChange}
                    />
                  </div>
                  <span className="usa-error-message" id="designedBy-error--message" role="alert">
                    <div>{vm.formValidation.formErrors['designedBy']}</div>
                  </span>
                </div>
                <div className="form-row-fixed-height">
                  <label className="usa-label" htmlFor="jobClass">
                    Job Class:
                  </label>
                  <select
                    className="usa-select"
                    name="jobClass"
                    id="jobClass"
                    aria-label="Job Class"
                    data-testid="the-SelectOption"
                    defaultValue={vm.projectSummaryForm.jobClass}
                    onChange={vm.handleInputChange}
                  >
                    <option value="0"> - select - </option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                  </select>
                </div>
              </div>
              <div className="col">
                <div className="form-row-fixed-height">
                  <label className="usa-label" htmlFor="projectType">
                    Project Type:
                  </label>
                  <input
                    className="form-control-plaintext px-2 readonly-text"
                    id="projectType"
                    aria-label="Project Type"
                    name="projectType"
                    type="text"
                    value={vm.project.projectClassId === 1 ? 'Design' : 'Analysis'}
                    readOnly
                  />
                </div>
                <div className="form-row-fixed-height">
                  <label className="usa-label" htmlFor="projectOwner">
                    Project Owner:
                  </label>
                  <input
                    className="form-control-plaintext px-2 readonly-text"
                    id="projectOwner"
                    aria-label="Project Owner"
                    name="projectOwner"
                    type="text"
                    value={vm.newOwnerName}
                    readOnly
                  />
                </div>
                <div className="form-row-fixed-height">
                  <label className="usa-label" htmlFor="county-state">
                    County/State:
                  </label>
                  <input
                    className="form-control-plaintext px-2 readonly-text"
                    id="county-state"
                    aria-label="County/State"
                    name="county-state"
                    type="text"
                    value={`${vm.project.county ? vm.project.county + ', ' : ''}${vm.project.state ? vm.project.state : ''
                      }`}
                    readOnly
                  />
                </div>
                <div className="form-row-fixed-height">
                  <label className="usa-label projectionInfo" htmlFor="projectionInformation">
                    Projection Information:
                  </label>
                  <div className="text-wrap px-2 readonly-text projectionInfo" >
                    {libraryStore.projectProjectionInfo}
                  </div>
                </div>
              </div>
            </div>
            <div className="form-row-fixed-height justify-content-end">
              <button className="usa-button usa-button--outline" onClick={vm.handleCancelEdits}>
                Close
              </button>
              <button className="usa-button" onClick={vm.handleSave}>
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export const ProjectSummary = observer(ProjectSummaryComponent);
